/**
 * Daily missions — deterministic pick of 3 from a pool of 5, seeded by local date.
 */

export type MissionId =
  | "play3"
  | "reach400"
  | "coins20"
  | "jumps8"
  | "beatBest";

export type MissionDef = {
  id: MissionId;
  label: string;
  target: number;
};

export const MISSION_POOL: MissionDef[] = [
  { id: "play3", label: "Finish 3 runs", target: 3 },
  { id: "reach400", label: "Reach 400 m in one run", target: 400 },
  { id: "coins20", label: "Collect 20 coins today", target: 20 },
  { id: "jumps8", label: "Jump 8 times today", target: 8 },
  { id: "beatBest", label: "Beat your best", target: 1 },
];

export const MISSION_REWARD = 40;
export const STREAK_BASE = 25;
export const STREAK_PER_DAY = 5;
export const STREAK_CAP_DAYS = 14;

export function localDayKey(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function daySeed(day: string): number {
  let h = 2166136261;
  for (let i = 0; i < day.length; i++) {
    h ^= day.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Mulberry32 — deterministic shuffle of the pool for `day`. */
export function missionsForDay(day = localDayKey()): MissionDef[] {
  const pool = [...MISSION_POOL];
  let s = daySeed(day);
  const rand = () => {
    s |= 0;
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool.slice(0, 3);
}

export function streakReward(streakCount: number): number {
  return STREAK_BASE + STREAK_PER_DAY * Math.min(Math.max(streakCount, 1), STREAK_CAP_DAYS);
}

/** Previous calendar day key (local). */
export function prevDayKey(day = localDayKey()): string {
  const [y, m, d] = day.split("-").map(Number);
  const dt = new Date(y!, m! - 1, d!);
  dt.setDate(dt.getDate() - 1);
  return localDayKey(dt);
}

export type GhostSample = {
  /** Distance (m) along the run when sampled. */
  d: number;
  x: number;
  y: number;
};

export const GHOST_MAX_SAMPLES = 500;
export const GHOST_SAMPLE_HZ = 12;
